import React, { useRef, useCallback } from 'react';
import { useDebouncedCallback } from 'use-debounce';
import { useTextField } from '@react-aria/textfield';
import useMergedRef from '@react-hook/merged-ref';
import { useHover } from '@react-aria/interactions';
import { mergeProps } from '@react-aria/utils';
import { omit } from '../../utils/index.js';
import { Field } from '../field/index.js';
import { useTheme } from '../../hooks/index.js';
import { InputContainerElement, InputElement } from './input.styles.js';
import { InputLoading } from './input-loading.styles.js';
export const TextField = React.forwardRef(({ dataAutomation = 'textfield', description, fullWidth, ...props }, forwardedRef) => {
    const theme = useTheme();
    const inputRef = useRef(null);
    const ref = useMergedRef(inputRef, forwardedRef);
    const { labelProps, inputProps, descriptionProps, errorMessageProps } = useTextField({
        ...props,
        description: typeof description === 'function' ? undefined : description,
        validationState: !props.errorMessage ? 'valid' : 'invalid',
    }, inputRef);
    const { hoverProps } = useHover(props);
    const onDebouncedChange = useDebouncedCallback((search) => {
        props.onDebouncedChange?.(search);
    }, theme.debounceTime[props.debounceTime || 'none']);
    const onChange = useCallback((event) => {
        inputProps.onChange?.(event);
        onDebouncedChange(event.target.value);
    }, [onDebouncedChange, inputProps.onChange]);
    return (React.createElement(Field, { ...omit(mergeProps(props, inputProps), 'onKeyDown', 'onBlur', 'id', 'role', 'aria-labelledby', 'aria-required'), containerRef: props.containerRef, label: {
            children: props.label,
            hiddenLabel: props.hiddenLabel,
            ...labelProps,
        }, error: {
            ...errorMessageProps,
            message: props.errorMessage,
        }, description: {
            ...descriptionProps,
            message: typeof description === 'function' ? description() : description,
        }, dataAutomation: dataAutomation },
        React.createElement(InputContainerElement, { ref: props.inputContainerRef, isErrored: !!props.errorMessage, isDisabled: !!props.isDisabled, isOnGray: !!props.isOnGray, fullWidth: fullWidth }, props.loading ? (React.createElement(InputLoading, null, props.value || props.placeholder)) : (React.createElement(InputElement, { ref: ref, ...mergeProps(omit(inputProps, 'onChange'), hoverProps), onChange: onChange, onKeyDown: props.onKeyDown, "data-automation": `${dataAutomation}/input`, role: props.role, tabIndex: props.tabIndex, "aria-controls": props['aria-controls'], "aria-expanded": props['aria-expanded'] })))));
});
//# sourceMappingURL=text-field.component.js.map